import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Expo, gsap } from "gsap";
import { useCallback, useLayoutEffect, useRef } from 'react';


const useStyles = makeStyles((theme: Theme) => ({
    jellyBlob: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '150px',
        height: '150px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mixBlendMode: 'multiply',
        background: theme.palette.custom.gradiantPrimary,
        borderRadius: '150px',
        transformOrigin: '50% 50%',
        transform: 'translate(-50%, -50%)',
        willChange: 'width, height, transform, border',
        zIndex: 999,
        pointerEvents: 'none',
        opacity: 0.5,
    },
    insideText: {
        width: '100%',
        opacity: 1,
        color: 'white',
        fontSize: '18px',
        textAlign: 'center',
        fontWeight: 600,
    }
}))

// Gsap Ticker Function
function useTicker(callback: () => void, paused?: boolean) {
    useLayoutEffect(() => {
        if (!paused && callback) {
            gsap.ticker.add(callback);
        }
        return () => {
            gsap.ticker.remove(callback);
        };
    }, [callback, paused]);
}

const EMPTY = {
    x: 0,
    y: 0,
    r: 0,
    sx: 0,
    sy: 0,
    rt: 0
};
function useInstance(value = {}) {
    const ref = useRef(EMPTY);
    if (ref.current === EMPTY) {
        ref.current = typeof value === "function" ? value() : value;
    }
    return ref.current;
}

// Function for Mouse Move Scale Change
function getScale(diffX: number, diffY: number) {
    const distance = Math.sqrt(Math.pow(diffX, 2) + Math.pow(diffY, 2));
    return Math.min(distance / 735, 0.35);
}

// Function For Mouse Movement Angle in Degrees
function getAngle(diffX: number, diffY: number) {
    return (Math.atan2(diffY, diffX) * 180) / Math.PI;
}

// Jelly Blob Function
const JellyBlob = () => {
    const classes = useStyles()
    // React Refs for Jelly Blob and Text
    const jellyRef = useRef(null);
    const textRef = useRef(null);

    // Save pos and velocity Objects
    const pos = useInstance(() => ({ x: 0, y: 0 }));
    const vel = useInstance(() => ({ x: 0, y: 0 }));
    const set = useInstance();

    // Set GSAP quick setter Values on useLayoutEffect Update
    useLayoutEffect(() => {
        set.x = gsap.quickSetter(jellyRef.current, "x", "px") as unknown as number;
        set.y = gsap.quickSetter(jellyRef.current, "y", "px") as unknown as number;
        set.r = gsap.quickSetter(jellyRef.current, "rotate", "deg") as unknown as number;
        set.sx = gsap.quickSetter(jellyRef.current, "scaleX") as unknown as number;
        set.sy = gsap.quickSetter(jellyRef.current, "scaleY") as unknown as number;
        set.rt = gsap.quickSetter(textRef.current, "rotate", "deg") as unknown as number;
    }, []);

    // Start Animation loop
    const loop = useCallback(() => {
        // Calculate angle and scale based on velocity
        var rotation = getAngle(vel.x, vel.y); // Mouse Move Angle
        var scale = getScale(vel.x, vel.y); // Blob Squeeze Amount

        // Set GSAP quick setter Values on Loop Function
        (set.x as unknown as Function)(pos.x);
        (set.y as unknown as Function)(pos.y);
        (set.r as unknown as Function)(rotation);
        (set.sx as unknown as Function)(1 + scale);
        (set.sy as unknown as Function)(1 - scale);
        (set.rt as unknown as Function)(-rotation);
    }, []);

    // Run on Mouse Move
    useLayoutEffect(() => {
        // Caluclate Everything Function
        const setFromEvent = (e: any) => {
            // Mouse X and Y
            const x = e.clientX;
            const y = e.clientY;

            // Animate Position and calculate Velocity with GSAP
            gsap.to(pos, {
                x: x,
                y: y,
                duration: 0.95,
                ease: Expo.easeOut,
                onUpdate: () => {
                    vel.x = x - pos.x;
                    vel.y = y - pos.y;
                }
            });

            loop();
        };

        window.addEventListener("mousemove", setFromEvent);
        return () => {
            window.removeEventListener("mousemove", setFromEvent);
        };
    }, []);

    useTicker(loop);

    // Return UI
    return (
        <div ref={jellyRef} id={"jelly-id"} className={classes.jellyBlob}>
            {/* <div ref={textRef} id={"text-id"} className={classes.insideText}>
                Hey There, I'm Lil Blob
            </div> */}
        </div>
        // <div className="container-div">
        // </div>
    );
};


export default JellyBlob